import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import { get } from "helpers/api_helper"
import { GET_ORDER_DETAILS } from "helpers/url_helper"

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

const displayValue = value => {
  if (value == null || value === "") return "-"
  return String(value)
}

const formatOrderTime = value => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const pad = n => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const formatDeliveryTime = order => {
  const dateValue =
    order?.estimatedDeliveryDate ||
    order?.promiseDateTime ||
    order?.pickupDate ||
    null
  if (!dateValue) return ""
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ""
  const pad = n => String(n).padStart(2, "0")
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`
  if (String(order?.orderType || "").toLowerCase() === "delivery") {
    return datePart
  }
  const timeHint =
    order?.pickupTime || (date.getHours() < 12 ? "AM" : "PM")
  return `${datePart} ${timeHint}`.trim()
}

const formatMoney = (amount, currency = "SAR") => {
  if (amount == null || amount === "") return `-`
  const value = Number(amount)
  if (Number.isNaN(value)) return `-`
  return `${value.toFixed(2)} ${currency}`
}

const formatWeight = weight => {
  if (weight == null || weight === "" || Number(weight) === 0) return ""
  return `${weight} g`
}

const buildMapsUrl = (lat, lng) => {
  if (lat == null || lng == null || lat === "" || lng === "") return ""
  return `https://www.google.com/maps/place/${lat}+${lng}`
}

const buildItemDescription = item => {
  const itemDoc = item?.itemId || {}
  const name = itemDoc.name || item?.name || ""
  const nameAr = itemDoc.nameAr || item?.nameAr || ""
  if (name && nameAr) return `${name} - ${nameAr}`
  return name || nameAr || ""
}

const resolveCustomer = order => {
  const recipient = order?.recipientInformation || {}
  const address = recipient.address || {}
  const user = order?.userId || {}
  const phone =
    recipient.recipientPhone ||
    (user.phoneCode && user.phoneNumber
      ? `${user.phoneCode}${user.phoneNumber}`
      : user.phone || "")

  return {
    name:
      recipient.recipientName ||
      user.name ||
      [user.firstName, user.lastName].filter(Boolean).join(" ") ||
      "",
    phone,
    province: address.district || address.city || "",
    street: address.street || address.address1 || "",
    postalCode: address.postal_code || "",
    location: buildMapsUrl(address.lat, address.lng),
  }
}

const resolveCurrency = order =>
  order?.splitPayments?.currency || order?.currency || "SAR"

const resolveOrderItems = order => {
  const items = Array.isArray(order?.orderItems) ? order.orderItems : []
  return items.map(row => {
    const itemDoc = row?.itemId || {}
    const qty = Number(row.quantity || 0)
    const price = Number(row.price || 0)
    const total =
      row.totalPrice != null ? Number(row.totalPrice) : price * qty
    return {
      barcode: itemDoc.SKU || row.SKU || "",
      description: buildItemDescription(row),
      weight: formatWeight(itemDoc.weight ?? row.weight),
      price,
      qty,
      total,
    }
  })
}

const resolvePaymentStatusLabel = status => {
  if (Number(status) === 0) return "Pending / قيد الانتظار"
  if (Number(status) === 1) return "Paid / مدفوع"
  if (Number(status) === 2) return "Refunded / مسترد"
  return "-"
}

const resolveOrderTypeLabel = orderType => {
  const type = String(orderType || "").toLowerCase()
  if (type === "delivery") return "Delivery / التوصيل"
  if (type === "pickup") return "Pickup / استلام"
  if (type === "curbside") return "Curbside / التسليم عند الرصيف"
  return orderType || "-"
}

const resolveOrderStatusLabel = (orderStatus, orderType) => {
  const status = Number(orderStatus)
  const isDelivery = String(orderType || "").toLowerCase() === "delivery"
  switch (status) {
    case 0:
      return "Pending / قيد الانتظار"
    case 1:
      return "Accepted / مقبول"
    case 2:
      return "Preparing / قيد التحضير"
    case 3:
      return isDelivery
        ? "Out for Delivery / خارج للتوصيل"
        : "Ready for Pickup / جاهز للاستلام"
    case 4:
      return isDelivery ? "Delivered / تم التوصيل" : "Picked Up / تم الاستلام"
    case 5:
      return "Cancelled / ملغى"
    default:
      return orderStatus != null && orderStatus !== "" ? String(orderStatus) : "-"
  }
}

const resolveBranchName = order => {
  const en = order?.branchName || order?.branchId?.name || ""
  const ar = order?.branchNameAr || order?.branchId?.nameAr || ""
  if (en && ar && en !== ar) return `${en} - ${ar}`
  return en || ar || "-"
}

const fetchLogoDataUrlFromApi = async () => {
  try {
    const response = await get("admin/tenants/branding/logo-data")
    const dataUrl = response?.data?.dataUrl
    return typeof dataUrl === "string" && dataUrl.startsWith("data:")
      ? dataUrl
      : ""
  } catch {
    return ""
  }
}

const cellStyle =
  "border:1px solid #000;border-radius:0 !important;padding:6px;font-size:11px;text-align:center;vertical-align:middle;"

const boldCellStyle = `${cellStyle}font-weight:700;`

const paymentLabelStyle =
  "border:none !important;padding:6px 8px 6px 0;font-size:11px;text-align:left;vertical-align:middle;width:50%;font-weight:600;"

const paymentValueStyle =
  "border:none !important;padding:6px 8px;font-size:11px;text-align:left;vertical-align:middle;width:50%;word-break:break-word;"

const buildOrderDetailHtml = (order, currency, options = {}) => {
  const { includeWalletAmount = false, logoUrl = "" } = options
  const customer = resolveCustomer(order)
  const items = resolveOrderItems(order)
  const split = order?.splitPayments || {}
  const orderNo = order?.invoiceNo || order?.orderId || order?._id || ""
  const deliveryTime = formatDeliveryTime(order)
  const orderTime = formatOrderTime(order?.createdAt || order?.orderDate)

  const infoRow = (enLabel, arLabel, value) => {
    const shown = displayValue(value) === "-" ? "" : displayValue(value)
    return `
    <tr>
      <td style="width:28%;padding:4px 0;font-size:12px;text-align:left;vertical-align:top;">${escapeHtml(enLabel)}</td>
      <td style="width:44%;padding:4px 8px;font-size:12px;text-align:center;vertical-align:top;word-break:break-word;font-weight:700;">${escapeHtml(shown)}</td>
      <td style="width:28%;padding:4px 0;font-size:12px;text-align:right;vertical-align:top;direction:rtl;">${escapeHtml(arLabel)}</td>
    </tr>
  `
  }

  const paymentRow = (label, valueHtml) => `
    <tr>
      <td style="${paymentLabelStyle}">${escapeHtml(label)}</td>
      <td style="${paymentValueStyle}">${valueHtml}</td>
    </tr>
  `

  const paymentText = value => escapeHtml(displayValue(value))

  const paymentRows = [
    paymentRow("Coupon / القسيمة", paymentText(order?.coupon?.code || "-")),
    paymentRow(
      "Discount Type / نوع الخصم",
      paymentText(order?.discountType || "-")
    ),
    paymentRow("Total / المجموع", paymentText(formatMoney(split.total, currency))),
    paymentRow(
      "VAT Amount / مبلغ ضريبة القيمة المضافة",
      paymentText(formatMoney(split.vatAmount, currency))
    ),
    paymentRow(
      "VAT % / ضريبة القيمة المضافة %",
      paymentText(split.vatValue ?? "-")
    ),
    paymentRow(
      "Online / عبر الإنترنت",
      paymentText(formatMoney(split.gatewayAmount, currency))
    ),
    paymentRow(
      "Discounted Amount / المبلغ بعد الخصم",
      paymentText(formatMoney(split.discountedAmount, currency))
    ),
  ]

  if (includeWalletAmount) {
    paymentRows.push(
      paymentRow(
        "Wallet Amount / مبلغ المحفظة",
        paymentText(formatMoney(split.walletAmount, currency))
      )
    )
  }

  paymentRows.push(
    paymentRow(
      "Loyalty Amount / مبلغ الولاء",
      paymentText(formatMoney(split.rewardAmount, currency))
    ),
    paymentRow(
      "Loyalty Redeemed points / نقاط الولاء المستبدلة",
      paymentText(split.rewardPoints ?? "-")
    ),
    paymentRow(
      "Loyalty Earned Points / نقاط الولاء المكتسبة",
      paymentText(order?.earnPoints ?? "-")
    ),
    paymentRow(
      "Payment Method / طريقة الدفع",
      paymentText(order?.paymentMethod || "-")
    ),
    paymentRow(
      "Payment Status / حالة الدفع",
      paymentText(resolvePaymentStatusLabel(order?.paymentStatus))
    )
  )

  if (order?.paymentEvidenceUrl) {
    const evidenceUrl = String(order.paymentEvidenceUrl)
    paymentRows.push(
      paymentRow(
        "Payment Evidence / إثبات الدفع",
        `<a href="${escapeHtml(evidenceUrl)}" data-pdf-link="${escapeHtml(
          evidenceUrl
        )}" target="_blank" rel="noopener noreferrer" style="color:#0563C1;text-decoration:underline;font-weight:700;">View Evidence / عرض الإثبات</a>`
      )
    )
  }

  if (Number(order?.paymentStatus) === 2) {
    paymentRows.push(
      paymentRow(
        "Refund At / تم الاسترداد في",
        paymentText(
          formatOrderTime(order?.transaction?.refundInfo?.refundAt) || "-"
        )
      )
    )
  }

  paymentRows.push(
    paymentRow(
      "Payment Reference / مرجع الدفع",
      paymentText(order?.transaction?.transactionId || "-")
    )
  )

  const paymentSplitIndex = Math.ceil(paymentRows.length / 2)
  const leftPaymentRows = paymentRows.slice(0, paymentSplitIndex)
  const rightPaymentRows = paymentRows.slice(paymentSplitIndex)

  const renderPaymentTable = rows => `
    <table class="payment-table" style="width:100%;table-layout:fixed;border-collapse:collapse;border-spacing:0;border:none !important;">
      <tbody>
        ${rows.join("")}
      </tbody>
    </table>
  `

  const itemRows = items
    .map(
      row => `
      <tr>
        <td style="${boldCellStyle}">${escapeHtml(row.barcode)}</td>
        <td style="${cellStyle}">${escapeHtml(row.description)}</td>
        <td style="${cellStyle}">${escapeHtml(row.weight)}</td>
        <td style="${boldCellStyle}">${escapeHtml(
          formatMoney(row.price, currency)
        )}</td>
        <td style="${boldCellStyle}">${escapeHtml(row.qty)}</td>
        <td style="${cellStyle}">${escapeHtml(
          formatMoney(row.total, currency)
        )}</td>
      </tr>
    `
    )
    .join("")

  const logoHtml = logoUrl
    ? `<div style="text-align:center;margin:0 0 16px 0;">
        <img src="${escapeHtml(
          logoUrl
        )}" alt="Tenant Logo" style="max-height:90px;max-width:260px;object-fit:contain;display:inline-block;" />
      </div>`
    : ""

  return `
    <div id="order-detail-pdf-root" style="width:794px;padding:28px 32px;background:#fff;color:#000;font-family:Arial,'Segoe UI',Tahoma,sans-serif;box-sizing:border-box;">
      <style>
        #order-detail-pdf-root table.square-table,
        #order-detail-pdf-root table.square-table th,
        #order-detail-pdf-root table.square-table td,
        #order-detail-pdf-root table.square-table tr {
          border-radius: 0 !important;
        }
        #order-detail-pdf-root table.square-table {
          border-collapse: collapse !important;
          border-spacing: 0 !important;
        }
        #order-detail-pdf-root table.payment-table,
        #order-detail-pdf-root table.payment-table th,
        #order-detail-pdf-root table.payment-table td,
        #order-detail-pdf-root table.payment-table tr {
          border: none !important;
          border-radius: 0 !important;
        }
      </style>
      ${logoHtml}
      <div style="text-align:center;margin-bottom:18px;line-height:1.7;font-size:13px;">
        <div>Order Number - <strong>${escapeHtml(orderNo)}</strong> - رقم الطلب</div>
        <div>Delivery time - <strong>${escapeHtml(
          deliveryTime
        )}</strong> - وقت التوصيل</div>
        <div>Order time - <strong>${escapeHtml(orderTime)}</strong> - وقت الطلب</div>
      </div>

      <table style="width:100%;border-collapse:collapse;margin-bottom:22px;">
        <tbody>
          ${infoRow("Customer Name", "إسم العميل", customer.name)}
          ${infoRow("Phone Number", "رقم الهاتف", customer.phone)}
          ${infoRow("Branch Name", "اسم الفرع", resolveBranchName(order))}
          ${infoRow("Order Type", "نوع الطلب", resolveOrderTypeLabel(order?.orderType))}
          ${infoRow(
            "Order Status",
            "حالة الطلب",
            resolveOrderStatusLabel(order?.orderStatus, order?.orderType)
          )}
          ${
            String(order?.orderType || "").toLowerCase() === "pickup"
              ? ""
              : [
                  infoRow("Province", "المقاطعة", customer.province),
                  infoRow("Street", "الشارع", customer.street),
                  infoRow("Postal Code", "الرمز البريدي", customer.postalCode),
                  infoRow("Location", "الموقع", customer.location),
                ].join("")
          }
        </tbody>
      </table>

      <div style="font-size:13px;font-weight:700;margin:0 0 8px 0;">
        Items / العناصر
      </div>
      <table class="square-table" style="width:100%;border-collapse:collapse;border-spacing:0;table-layout:fixed;margin-bottom:22px;border-radius:0 !important;">
        <thead>
          <tr>
            <th style="${cellStyle}width:16%;">Barcode</th>
            <th style="${cellStyle}width:34%;">Description / الوصف</th>
            <th style="${cellStyle}width:12%;">Weight / الوزن</th>
            <th style="${cellStyle}width:14%;">Price / السعر</th>
            <th style="${cellStyle}width:10%;">Qty / الكمية</th>
            <th style="${cellStyle}width:14%;">Total / المجموع</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows || `<tr><td colspan="6" style="${cellStyle}">-</td></tr>`}
        </tbody>
      </table>

      <div style="font-size:13px;font-weight:700;margin:0 0 8px 0;">
        Payments Details / تفاصيل الدفعات
      </div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;width:100%;">
        <div style="flex:1;min-width:0;">
          ${renderPaymentTable(leftPaymentRows)}
        </div>
        <div style="flex:1;min-width:0;">
          ${renderPaymentTable(rightPaymentRows)}
        </div>
      </div>
    </div>
  `
}

const waitForImages = async root => {
  const images = Array.from(root.querySelectorAll("img"))
  await Promise.all(
    images.map(
      img =>
        new Promise(resolve => {
          if (img.complete && img.naturalWidth > 0) {
            resolve()
            return
          }
          const done = () => resolve()
          img.addEventListener("load", done, { once: true })
          img.addEventListener("error", done, { once: true })
        })
    )
  )
}

const collectPdfLinks = (root, usableWidth) => {
  const scale = usableWidth / root.offsetWidth
  const rootRect = root.getBoundingClientRect()
  return Array.from(root.querySelectorAll("[data-pdf-link]")).map(el => {
    const rect = el.getBoundingClientRect()
    return {
      url: el.getAttribute("data-pdf-link"),
      x: (rect.left - rootRect.left) * scale,
      y: (rect.top - rootRect.top) * scale,
      w: Math.max(rect.width * scale, 48),
      h: Math.max(rect.height * scale, 14),
    }
  })
}

const addLinksToPdf = (pdf, links, margin, pageHeight, imgHeight) => {
  if (!links?.length) return
  const contentHeight = pageHeight - margin * 2
  const pageCount = Math.max(1, Math.ceil(imgHeight / contentHeight))

  links.forEach(link => {
    if (!link?.url) return
    for (let page = 0; page < pageCount; page++) {
      const pageOffset = page * contentHeight
      const linkTop = link.y
      const linkBottom = link.y + link.h
      if (linkBottom <= pageOffset || linkTop >= pageOffset + contentHeight) {
        continue
      }
      const y = margin + (linkTop - pageOffset)
      pdf.setPage(page + 1)
      pdf.link(margin + link.x, y, link.w, link.h, { url: link.url })
    }
  })
}

const renderHtmlToPdf = async (html, fileName) => {
  const wrapper = document.createElement("div")
  wrapper.style.position = "fixed"
  wrapper.style.left = "-10000px"
  wrapper.style.top = "0"
  wrapper.style.zIndex = "-1"
  wrapper.innerHTML = html
  document.body.appendChild(wrapper)

  try {
    const node = wrapper.querySelector("#order-detail-pdf-root")
    await waitForImages(node)

    // Raster PDF: keep scale modest. scale:2 + PNG was producing 10MB+ files.
    const canvas = await html2canvas(node, {
      scale: 1.5,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
    })

    const pdf = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 24
    const usableWidth = pageWidth - margin * 2
    const contentHeight = pageHeight - margin * 2
    const imgHeight = (canvas.height * usableWidth) / canvas.width
    const links = collectPdfLinks(node, usableWidth)

    // Slice the canvas per page so page breaks never repeat overlapping content.
    const pageSliceHeightPx = Math.ceil(
      (contentHeight * canvas.width) / usableWidth
    )
    let sourceY = 0
    let pageIndex = 0

    while (sourceY < canvas.height) {
      if (pageIndex > 0) {
        pdf.addPage()
      }

      const sliceHeightPx = Math.min(
        pageSliceHeightPx,
        canvas.height - sourceY
      )
      const pageCanvas = document.createElement("canvas")
      pageCanvas.width = canvas.width
      pageCanvas.height = sliceHeightPx
      const ctx = pageCanvas.getContext("2d")
      ctx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      )

      const sliceData = pageCanvas.toDataURL("image/jpeg", 0.72)
      const slicePdfHeight = (sliceHeightPx * usableWidth) / canvas.width
      pdf.addImage(
        sliceData,
        "JPEG",
        margin,
        margin,
        usableWidth,
        slicePdfHeight
      )

      sourceY += sliceHeightPx
      pageIndex += 1
    }

    addLinksToPdf(pdf, links, margin, pageHeight, imgHeight)
    pdf.save(fileName)
  } finally {
    document.body.removeChild(wrapper)
  }
}

/**
 * Fetch order details and download a bilingual order-details PDF.
 * @param {string} orderId
 * @param {{ includeWalletAmount?: boolean }} [options]
 */
export const downloadOrderDetailPdf = async (orderId, options = {}) => {
  if (!orderId) {
    throw new Error("Order id is required")
  }

  const response = await get(`${GET_ORDER_DETAILS}/${orderId}`)
  if (!(response?.statusCode === 200 && response?.status && response?.data)) {
    throw new Error(response?.msg || "Failed to load order details")
  }

  const order = response.data
  const currency = resolveCurrency(order)
  const orderNo = order?.invoiceNo || order?._id || "order"
  // Object-storage logo URLs are cross-origin and fail CORS in the browser.
  // Use same-origin branding/logo-data (server fetches logo) instead.
  const logoDataUrl = await fetchLogoDataUrlFromApi()
  const html = buildOrderDetailHtml(order, currency, {
    ...options,
    logoUrl: logoDataUrl,
  })
  await renderHtmlToPdf(html, `order-details-${orderNo}.pdf`)
  return order
}
